import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  ButtonGroup,
  Modal,
  OverlayTrigger,
  Spinner,
  Tooltip,
} from "react-bootstrap";
import { useAuth } from "../../../providers/authprovider";
import { formatDateInput } from "../utils/helpers";

const PAGE_SIZE = 30;

const FIELD_LABELS = {
  date: "Data",
  start_time: "Start",
  end_time: "Koniec",
  work_norm: "Norma",
  approved: "Zatwierdzony",
  type: "Typ",
  deleted: "Usunięty",
};

const SOURCE_FILTERS = [
  { value: "", label: "Wszystkie" },
  { value: "worktime", label: "Wiersz" },
  { value: "note", label: "Notatka" },
];

// Zmiana pochodząca z notatki wygląda w changes tak samo jak ręczna edycja wiersza —
// rozróżnia je dopiero pole source.
const SOURCE_LABELS = {
  note: { text: "Z notatki", variant: "info" },
  worktime: { text: "Edycja wiersza", variant: "secondary" },
};

const ACTION_LABELS = {
  create: { text: "Utworzenie", variant: "success" },
  update: { text: "Zmiana", variant: "primary" },
  delete: { text: "Usunięcie", variant: "danger" },
};

function formatValue(value) {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "boolean") return value ? "tak" : "nie";

  const text = String(value);

  // daty ISO skracamy do samego dnia, resztę (godziny, typ) zostawiamy jak jest
  if (/^\d{4}-\d{2}-\d{2}T/.test(text)) return formatDateInput(text);

  return text;
}

function formatTimestamp(value) {
  if (!value) return "";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return String(value);

  return date.toLocaleString("pl-PL");
}

export default function WorkTimeHistoryModal({ show, worktimeId, userId, onHide }) {
  const { workTime } = useAuth();

  const [items, setItems] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [source, setSource] = useState("");

  const loadPage = useCallback(
    async (nextCursor) => {
      setLoading(true);
      setError("");

      const result = await workTime.fetchWorktimeHistory({
        limit: PAGE_SIZE,
        cursor: nextCursor,
        worktimeId,
        userId,
        source: source || null,
      });

      setLoading(false);

      if (!result?.success) {
        setError(result?.error || "Nie udało się pobrać historii zmian.");
        return;
      }

      const content = result.content || {};

      setItems((prev) =>
        nextCursor ? [...prev, ...(content.items || [])] : content.items || [],
      );
      setHasMore(!!content.hasMore);
      setCursor(content.nextCursor ?? null);
    },
    [workTime, worktimeId, userId, source],
  );

  // Zmiana filtra źródła przebudowuje loadPage, więc lista ładuje się od zera.
  useEffect(() => {
    if (!show) return;

    setItems([]);
    setCursor(null);
    setHasMore(false);
    loadPage(null);
  }, [show, loadPage]);

  const isEmpty = !loading && !error && items.length === 0;

  return (
    <Modal show={show} onHide={onHide} centered size="lg" scrollable>
      <Modal.Header closeButton>
        <Modal.Title>Historia zmian</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <ButtonGroup size="sm" className="mb-3">
          {SOURCE_FILTERS.map((filter) => (
            <Button
              key={filter.value || "all"}
              variant={
                source === filter.value ? "secondary" : "outline-secondary"
              }
              onClick={() => setSource(filter.value)}
              disabled={loading}
            >
              {filter.label}
            </Button>
          ))}
        </ButtonGroup>

        {error ? <Alert variant="danger">{error}</Alert> : null}

        {isEmpty ? <div className="text-muted">Brak zmian do wyświetlenia.</div> : null}

        <div className="d-flex flex-column gap-3">
          {items.map((item) => (
            <HistoryEntry key={item.id || item.i} item={item} />
          ))}
        </div>

        {loading ? (
          <div className="text-center py-3">
            <Spinner animation="border" size="sm" />
          </div>
        ) : null}

        {hasMore && !loading ? (
          <div className="text-center pt-3">
            <Button variant="outline-secondary" onClick={() => loadPage(cursor)}>
              Pokaż starsze
            </Button>
          </div>
        ) : null}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Zamknij
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

function HistoryEntry({ item }) {
  const action = ACTION_LABELS[item.action] || {
    text: item.action,
    variant: "secondary",
  };

  const sourceLabel = SOURCE_LABELS[item.source] || null;

  return (
    <div className="border-bottom pb-3">
      <div className="d-flex align-items-start justify-content-between gap-2">
        <div className="d-flex flex-wrap align-items-center gap-2">
          <Badge bg={action.variant}>{action.text}</Badge>

          {sourceLabel ? (
            <Badge bg={sourceLabel.variant}>{sourceLabel.text}</Badge>
          ) : null}
        </div>

        <OverlayTrigger
          placement="top"
          overlay={
            <Tooltip id={`worktime-history-author-${item.id || item.i}`}>
              Kto i kiedy dokonał zmiany
            </Tooltip>
          }
        >
          <Badge bg="light" text="dark" className="fw-normal" style={{ cursor: "help" }}>
            {item.user_name || "System"} · {formatTimestamp(item.created_at)}
          </Badge>
        </OverlayTrigger>
      </div>

      <div className="d-flex flex-wrap align-items-center gap-2 mt-1 mb-2">
        <span className="fw-semibold">{item.worktime_user_name || "—"}</span>

        <span className="text-muted small">
          dzień {formatDateInput(item.worktime_date)}
        </span>
      </div>

      {item.changes?.length ? (
        <ul className="mb-0 ps-3 small">
          {item.changes.map((change, index) => (
            <li key={`${change.field}-${index}`}>
              {FIELD_LABELS[change.field] || change.field}:{" "}
              <span className="text-decoration-line-through text-muted">
                {formatValue(change.from)}
              </span>{" "}
              → <span className="fw-semibold">{formatValue(change.to)}</span>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
